import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    PiArrowCounterClockwise,
    PiArrowsClockwise,
    PiChatCircle,
    PiCheck,
    PiCheckCircle,
    PiClipboardText,
    PiCube,
    PiEnvelopeBold,
    PiEye,
    PiFunnel,
    PiInfo,
    PiLightning,
    PiMagnifyingGlass,
    PiMapPin,
    PiPencil,
    PiPlus,
    PiTrash,
    PiXCircle,
    PiTranslateBold,
} from 'react-icons/pi';
import { Badge, Button } from '@v2/design-system';
import api from '@v2/lib/api';
import type { ShipmentContainerRow, ShipmentTaskRow } from './types';
import { stageBadgeColor } from './taskStageColor';
import ConferenceSheetImportButton from './ConferenceSheetImportButton';
import ProtheusSipoExportButton from './ProtheusSipoExportButton';
import TaskModals from './TaskModals';
import SendCustomsEmailModal from './SendCustomsEmailModal';

function hexToRgba(hex: string, alpha: number): string {
    const clean = hex.replace('#', '');
    const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
}

type ViewMode = 'tasks' | 'containers';
type ModalKind = 'hooks' | 'detail' | 'comments' | 'complete' | 'edit' | 'create';

type Props = {
    shipmentId: number;
    tasks: ShipmentTaskRow[];
    containers?: ShipmentContainerRow[];
    onRefresh: () => void;
    view?: ViewMode;
    onViewChange?: (v: ViewMode) => void;
    hideToggle?: boolean;
};

type GroupRowsProps = {
    shipmentId: number;
    group: { stage: string; label: string; tasks: ShipmentTaskRow[] };
    selected: Set<number>;
    sectorPopover: number | null;
    emailModalTaskId: number | null;
    onToggleOne: (id: number) => void;
    onSectorPopover: (id: number | null) => void;
    onOpenModal: (task: ShipmentTaskRow, kind: ModalKind) => void;
    onDestroy: (id: number) => void;
    onRefresh: () => void;
    onOpenEmailModal: (taskId: number) => void;
};

function uniqueTasksById(tasks: ShipmentTaskRow[]): ShipmentTaskRow[] {
    const seen = new Set<number>();
    return tasks.filter((t) => {
        if (seen.has(t.id)) {
            return false;
        }
        seen.add(t.id);
        return true;
    });
}

function groupByStage(tasks: ShipmentTaskRow[]): { stage: string; label: string; tasks: ShipmentTaskRow[] }[] {
    const order: string[] = [];
    const map = new Map<string, ShipmentTaskRow[]>();
    for (const t of tasks) {
        if (!map.has(t.stage)) {
            map.set(t.stage, []);
            order.push(t.stage);
        }
        map.get(t.stage)!.push(t);
    }
    return order.map((stage) => ({
        stage,
        label: map.get(stage)![0]?.stage_label ?? stage,
        tasks: map.get(stage)!,
    }));
}

export default function ShipmentTasksPanel({
    shipmentId,
    tasks,
    containers = [],
    onRefresh,
    view: viewProp,
    onViewChange,
    hideToggle = false,
}: Props) {
    const normalizedTasks = useMemo(() => uniqueTasksById(tasks), [tasks]);
    const [internalView, setInternalView] = useState<ViewMode>('tasks');
    const view = viewProp ?? internalView;
    const setView = (v: ViewMode) => {
        if (onViewChange) onViewChange(v);
        else setInternalView(v);
    };
    const [search, setSearch] = useState('');
    const [stageFilter, setStageFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'completed'>('all');
    const [showFilters, setShowFilters] = useState(false);
    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [modalTask, setModalTask] = useState<ShipmentTaskRow | null>(null);
    const [modalKind, setModalKind] = useState<ModalKind | null>(null);
    const [sectorPopover, setSectorPopover] = useState<number | null>(null);
    const [emailModalTaskId, setEmailModalTaskId] = useState<number | null>(null);

    const filtered = useMemo(() => {
        let list = normalizedTasks;
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(
                (t) =>
                    t.title.toLowerCase().includes(q) ||
                    (t.responsible ?? '').toLowerCase().includes(q) ||
                    t.stage_label.toLowerCase().includes(q),
            );
        }
        if (stageFilter) list = list.filter((t) => t.stage === stageFilter);
        if (statusFilter === 'pending') list = list.filter((t) => !t.is_completed);
        if (statusFilter === 'completed') list = list.filter((t) => t.is_completed);
        return list;
    }, [normalizedTasks, search, stageFilter, statusFilter]);

    const groups = useMemo(() => groupByStage(filtered), [filtered]);
    const stageOptions = useMemo(() => {
        const seen = new Map<string, string>();
        for (const t of normalizedTasks) seen.set(t.stage, t.stage_label);
        return Array.from(seen.entries());
    }, [normalizedTasks]);

    const activeFilterCount = [stageFilter, statusFilter !== 'all' ? statusFilter : ''].filter(Boolean).length;

    const syncResponsible = useMutation({
        mutationFn: async () => {
            const { data } = await api.post<{ message: string }>(`/shipments/${shipmentId}/tasks/sync-responsible`);
            return data;
        },
        onSuccess: (data) => {
            toast.success(data.message);
            onRefresh();
        },
    });

    const bulkComplete = useMutation({
        mutationFn: async (ids: number[]) => {
            const { data } = await api.post<{ message: string }>(`/shipments/${shipmentId}/tasks/bulk-complete`, { ids });
            return data;
        },
        onSuccess: (data) => {
            toast.success(data.message);
            setSelected(new Set());
            onRefresh();
        },
    });

    const bulkDestroy = useMutation({
        mutationFn: async (ids: number[]) => {
            const { data } = await api.post<{ message: string }>(`/shipments/${shipmentId}/tasks/bulk-destroy`, { ids });
            return data;
        },
        onSuccess: (data) => {
            toast.success(data.message);
            setSelected(new Set());
            onRefresh();
        },
    });

    const destroyTask = useMutation({
        mutationFn: async (taskId: number) => {
            await api.delete(`/shipments/${shipmentId}/tasks/${taskId}`);
        },
        onSuccess: () => {
            toast.success('Tarefa excluída');
            onRefresh();
        },
    });

    const openModal = (task: ShipmentTaskRow, kind: ModalKind) => {
        setModalTask(task);
        setModalKind(kind);
    };

    const closeModal = () => {
        setModalTask(null);
        setModalKind(null);
    };

    const toggleAll = (ids: number[], checked: boolean) => {
        setSelected((prev) => {
            const next = new Set(prev);
            for (const id of ids) {
                if (checked) next.add(id);
                else next.delete(id);
            }
            return next;
        });
    };

    const toggleOne = (id: number) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const pendingSelectable = filtered.filter((t) => !t.is_completed && !t.has_requirements).map((t) => t.id);
    const allSelected = pendingSelectable.length > 0 && pendingSelectable.every((id) => selected.has(id));

    return (
        <div className="space-y-4">
            {!hideToggle && (
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
                <div className="inline-flex rounded-full border border-gray-200 bg-gray-50 p-1">
                    <button
                        type="button"
                        onClick={() => setView('tasks')}
                        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium ${
                            view === 'tasks' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-600'
                        }`}
                    >
                        <PiClipboardText className="h-4 w-4" />
                        Tarefas
                        <span className="rounded-full bg-orange-100 px-1.5 text-[10px] font-bold text-orange-700">{normalizedTasks.length}</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setView('containers')}
                        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium ${
                            view === 'containers' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                        }`}
                    >
                        <PiCube className="h-4 w-4" />
                        Containers
                        <span className="rounded-full bg-gray-200 px-1.5 text-[10px] font-bold text-gray-600">{containers.length}</span>
                    </button>
                </div>
            </div>
            )}

            {view === 'containers' ? (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                            <tr>
                                <th className="px-3 py-2 text-left">Container</th>
                                <th className="px-3 py-2 text-left">Tipo</th>
                                <th className="px-3 py-2 text-left">Lacre</th>
                                <th className="px-3 py-2 text-left">Cubagem</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {containers.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-3 py-6 text-center text-gray-500">
                                        Nenhum container cadastrado.
                                    </td>
                                </tr>
                            ) : (
                                containers.map((c) => (
                                    <tr key={c.id}>
                                        <td className="px-3 py-2 font-medium">{c.container_number}</td>
                                        <td className="px-3 py-2">{c.type || '—'}</td>
                                        <td className="px-3 py-2">{c.seal_number || '—'}</td>
                                        <td className="px-3 py-2">
                                            <ContainerFill cbm={c.cbm} capacity={c.cbm_capacity} />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            ) : (
                <>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <h3 className="text-lg font-semibold text-gray-900">Tarefas</h3>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                    setModalTask(null);
                                    setModalKind('create');
                                }}
                            >
                                <PiPlus className="me-1.5 h-4 w-4" />
                                Nova tarefa
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                isLoading={syncResponsible.isPending}
                                onClick={() => syncResponsible.mutate()}
                            >
                                <PiArrowsClockwise className="me-1.5 h-4 w-4" />
                                Sincronizar Responsáveis
                            </Button>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center justify-end gap-2">
                        <div className="relative">
                            <PiMagnifyingGlass className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            <input
                                type="search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Pesquisar"
                                className="rounded-lg border border-gray-300 py-2 ps-9 pe-3 text-sm"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={() => setShowFilters((v) => !v)}
                            className="relative inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                            <PiFunnel className="h-4 w-4" />
                            {activeFilterCount > 0 && (
                                <span className="absolute -end-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-orange-500 text-[10px] font-bold text-white">
                                    {activeFilterCount}
                                </span>
                            )}
                        </button>
                    </div>

                    {showFilters && (
                        <div className="flex flex-wrap gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                            <label className="text-sm">
                                <span className="mb-1 block text-xs text-gray-500">Estágio</span>
                                <select
                                    value={stageFilter}
                                    onChange={(e) => setStageFilter(e.target.value)}
                                    className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                                >
                                    <option value="">Todos</option>
                                    {stageOptions.map(([value, label]) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="text-sm">
                                <span className="mb-1 block text-xs text-gray-500">Status</span>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                                    className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                                >
                                    <option value="all">Todos</option>
                                    <option value="pending">Pendentes</option>
                                    <option value="completed">Concluídas</option>
                                </select>
                            </label>
                        </div>
                    )}

                    {selected.size > 0 && (
                        <div className="flex items-center gap-2 rounded-lg bg-orange-50 px-3 py-2 text-sm">
                            <span className="text-orange-800">{selected.size} selecionada(s)</span>
                            <Button
                                size="sm"
                                className="bg-green-600 text-white hover:bg-green-700"
                                isLoading={bulkComplete.isPending}
                                onClick={() => bulkComplete.mutate(Array.from(selected))}
                            >
                                <PiCheck className="me-1 h-4 w-4" />
                                Concluir selecionadas
                            </Button>
                            <Button
                                size="sm"
                                className="bg-red-600 text-white hover:bg-red-700"
                                isLoading={bulkDestroy.isPending}
                                onClick={() => bulkDestroy.mutate(Array.from(selected))}
                            >
                                <PiTrash className="me-1 h-4 w-4" />
                                Excluir selecionadas
                            </Button>
                            <Button size="sm" variant="text" onClick={() => setSelected(new Set())}>
                                Limpar
                            </Button>
                        </div>
                    )}

                    <div className="max-h-[32rem] overflow-auto">
                        <table className="min-w-full text-sm">
                            <thead className="sticky top-0 z-10 bg-white text-xs uppercase text-gray-500 dark:bg-zinc-900">
                                <tr>
                                    <th className="w-8 py-2">
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            onChange={(e) => toggleAll(pendingSelectable, e.target.checked)}
                                            className="rounded border-gray-300"
                                        />
                                    </th>
                                    <th className="w-8 py-2" />
                                    <th className="py-2 text-left">Estágio</th>
                                    <th className="py-2 text-left">Tarefa</th>
                                    <th className="py-2 text-left">Setor</th>
                                    <th className="py-2 text-left">Mod</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groups.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="py-8 text-center text-gray-500">
                                            Nenhuma tarefa encontrada.
                                        </td>
                                    </tr>
                                ) : (
                                    groups.map((group) => (
                                        <GroupRows
                                            key={group.stage}
                                            shipmentId={shipmentId}
                                            group={group}
                                            selected={selected}
                                            sectorPopover={sectorPopover}
                                            emailModalTaskId={emailModalTaskId}
                                            onToggleOne={toggleOne}
                                            onSectorPopover={setSectorPopover}
                                            onOpenModal={openModal}
                                            onDestroy={(id) => destroyTask.mutate(id)}
                                            onRefresh={onRefresh}
                                            onOpenEmailModal={setEmailModalTaskId}
                                        />
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            <TaskModals
                shipmentId={shipmentId}
                task={modalTask}
                kind={modalKind}
                onClose={closeModal}
                onUpdated={onRefresh}
            />

            <SendCustomsEmailModal
                shipmentId={shipmentId}
                open={emailModalTaskId !== null}
                onClose={() => setEmailModalTaskId(null)}
            />
        </div>
    );
}

function GroupRows({
    shipmentId,
    group,
    selected,
    sectorPopover,
    emailModalTaskId: _emailModalTaskId,
    onToggleOne,
    onSectorPopover,
    onOpenModal,
    onDestroy,
    onRefresh,
    onOpenEmailModal,
}: GroupRowsProps) {
    return (
        <>
            <tr>
                <td
                    colSpan={6}
                    className="px-3 py-1.5 text-xs font-semibold uppercase"
                    style={
                        group.tasks[0]?.stage_row_color
                            ? {
                                backgroundColor: hexToRgba(group.tasks[0].stage_row_color, 0.15),
                                color: group.tasks[0].stage_row_color,
                                borderLeft: `3px solid ${group.tasks[0].stage_row_color}`,
                            }
                            : { backgroundColor: '#f3f4f6', color: '#4b5563', borderLeft: '3px solid #d1d5db' }
                    }
                >
                    {group.label}
                </td>
            </tr>
            {group.tasks.map((task) => (
                <tr key={task.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="py-2 align-middle">
                        {!task.is_completed && !task.has_requirements && (
                            <input
                                type="checkbox"
                                checked={selected.has(task.id)}
                                onChange={() => onToggleOne(task.id)}
                                className="rounded border-gray-300"
                            />
                        )}
                    </td>
                    <td className="py-2 align-middle">
                        {task.is_completed ? (
                            <PiCheckCircle className="h-5 w-5 text-green-500" title="Concluída" />
                        ) : (
                            <PiXCircle className="h-5 w-5 text-gray-300" title="Pendente" />
                        )}
                    </td>
                    <td className="py-2 align-middle">
                        <Badge variant="flat" color={stageBadgeColor(task.stage_color)}>
                            {task.stage_label}
                        </Badge>
                    </td>
                    <td className="py-2 align-middle">
                        <span className={task.is_completed ? 'text-gray-400 line-through' : 'font-semibold text-gray-900'}>{task.title}</span>
                        <SlaChip task={task} />
                    </td>
                    <td className="relative py-2 align-middle">
                        <button
                            type="button"
                            onClick={() => onSectorPopover(sectorPopover === task.id ? null : task.id)}
                            className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-0.5 text-xs font-medium text-sky-800"
                        >
                            {task.responsible || '—'}
                            <PiMagnifyingGlass className="h-3 w-3 opacity-60" />
                        </button>
                        {sectorPopover === task.id && (
                            <div className="absolute z-10 mt-1 w-56 rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
                                <p className="text-xs font-semibold text-gray-500">Responsável</p>
                                <p className="text-sm font-medium text-gray-900">{task.assigned_user?.name || 'Não atribuído'}</p>
                                {task.assigned_user?.email && (
                                    <p className="text-xs text-gray-500">{task.assigned_user.email}</p>
                                )}
                                {task.assigned_user?.job_title && (
                                    <p className="mt-1 text-xs text-gray-600">{task.assigned_user.job_title}</p>
                                )}
                            </div>
                        )}
                    </td>
                    <td className="py-2 align-middle">
                        <div className="flex items-center gap-0.5">
                            {task.is_auto && (
                                <span title="Automático" className="p-1 text-amber-500">
                                    <PiLightning className="h-4 w-4" />
                                </span>
                            )}
                            {(task.actions.show_hooks || !!task.sla_days) && (
                                <IconBtn title="Como fazer e prazo" onClick={() => onOpenModal(task, 'hooks')}>
                                    <PiInfo className="h-4 w-4" />
                                </IconBtn>
                            )}
                            {task.actions.show_po && task.actions.po_url && (
                                <a href={task.actions.po_url} title="Montar PO" className="p-1 text-primary hover:bg-primary/10 rounded">
                                    <PiClipboardText className="h-4 w-4" />
                                </a>
                            )}
                            {task.actions.show_receive_cd && task.actions.receive_cd_url && (
                                <a href={task.actions.receive_cd_url} title="Alocar no CD" className="p-1 text-green-600 hover:bg-green-50 rounded">
                                    <PiMapPin className="h-4 w-4" />
                                </a>
                            )}
                            {task.actions.show_conference && task.actions.conference_url && (
                                <a
                                    href={task.actions.conference_url}
                                    title="Romaneio CD"
                                    className="p-1 text-violet-700 hover:bg-violet-50 rounded"
                                >
                                    <PiCube className="h-4 w-4" />
                                </a>
                            )}
                            {task.actions.can_import_conference_sheet && (
                                <ConferenceSheetImportButton
                                    shipmentId={shipmentId}
                                    iconOnly
                                    onSuccess={onRefresh}
                                />
                            )}
                            {task.actions.show_check && task.actions.check_url && (
                                <a
                                    href={task.actions.check_url}
                                    title="Conferência linha a linha"
                                    className="p-1 text-sky-700 hover:bg-sky-50 rounded"
                                >
                                    <PiClipboardText className="h-4 w-4" />
                                </a>
                            )}
                            {task.actions.show_pi_check && task.actions.pi_check_url && (
                                <a href={task.actions.pi_check_url} title="Validar PI x PO" className="p-1 text-emerald-700 hover:bg-emerald-50 rounded">
                                    <PiCheckCircle className="h-4 w-4" />
                                </a>
                            )}
                            {task.actions.show_protheus_sipo && <ProtheusSipoExportButton shipmentId={shipmentId} />}
                            {task.actions.show_customs_translation && task.actions.customs_translation_pdf_url && (
                                <a
                                    href={task.actions.customs_translation_pdf_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Gerar PDF de Tradução Aduaneira"
                                    className="p-1 text-amber-600 hover:bg-amber-50 rounded"
                                >
                                    <PiTranslateBold className="h-4 w-4" />
                                </a>
                            )}
                            {task.actions.show_send_customs_email && (
                                <IconBtn
                                    title="Enviar e-mail ao despachante"
                                    onClick={() => onOpenEmailModal(task.id)}
                                >
                                    <PiEnvelopeBold className="h-4 w-4 text-sky-600" />
                                </IconBtn>
                            )}
                            <IconBtn
                                title="Comentários"
                                onClick={() => onOpenModal(task, 'comments')}
                                badge={task.comments_count}
                            >
                                <PiChatCircle className="h-4 w-4" />
                            </IconBtn>
                            {task.actions.show_freight_intake && task.actions.freight_intake_url ? (
                                <a
                                    href={task.actions.freight_intake_url}
                                    title="Ver cotação de frete"
                                    className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                                >
                                    <PiEye className="h-4 w-4" />
                                </a>
                            ) : (
                                <IconBtn title="Ver tarefa" onClick={() => onOpenModal(task, 'detail')}>
                                    <PiEye className="h-4 w-4" />
                                </IconBtn>
                            )}
                            <IconBtn
                                title={task.is_completed ? 'Reabrir' : 'Concluir'}
                                onClick={() => onOpenModal(task, 'complete')}
                            >
                                {task.is_completed ? (
                                    <PiArrowCounterClockwise className="h-4 w-4 text-amber-600" />
                                ) : (
                                    <PiCheck className="h-4 w-4 text-green-600" />
                                )}
                            </IconBtn>
                            <IconBtn title="Editar" onClick={() => onOpenModal(task, 'edit')}>
                                <PiPencil className="h-4 w-4" />
                            </IconBtn>
                            <IconBtn
                                title="Excluir"
                                onClick={() => {
                                    if (window.confirm('Excluir esta tarefa?')) onDestroy(task.id);
                                }}
                            >
                                <PiTrash className="h-4 w-4 text-red-500" />
                            </IconBtn>
                        </div>
                    </td>
                </tr>
            ))}
        </>
    );
}

function ContainerFill({
    cbm,
    capacity,
}: {
    cbm?: string | number | null;
    capacity?: number | null;
}) {
    const used = cbm == null || cbm === '' ? null : Number(cbm);
    const cap = capacity && capacity > 0 ? capacity : 60;
    if (used == null || Number.isNaN(used)) {
        return <span className="text-gray-400">— / {cap} m³</span>;
    }
    const pct = Math.min(100, Math.round((used / cap) * 100));

    return (
        <div className="min-w-[8rem]">
            <div className="mb-1 text-xs text-gray-700">
                {used.toLocaleString('pt-BR', { maximumFractionDigits: 3 })} / {cap} m³
                <span className="ms-1 text-gray-400">({pct}%)</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
                <div
                    className={`h-full rounded-full ${pct >= 100 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
}

function IconBtn({
    children,
    title,
    onClick,
    badge,
}: {
    children: React.ReactNode;
    title: string;
    onClick?: () => void;
    badge?: number;
}) {
    return (
        <button
            type="button"
            title={title}
            onClick={onClick}
            className="relative rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
        >
            {children}
            {badge != null && badge > 0 && (
                <span className="absolute -end-0.5 -top-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-primary px-0.5 text-[9px] font-bold text-white">
                    {badge}
                </span>
            )}
        </button>
    );
}

const SLA_TONE: Record<string, string> = {
    overdue: 'bg-rose-50 text-rose-700 ring-rose-200',
    due_today: 'bg-amber-50 text-amber-800 ring-amber-200',
    due_soon: 'bg-amber-50 text-amber-700 ring-amber-200',
    on_time: 'bg-gray-50 text-gray-600 ring-gray-200',
    waiting: 'bg-gray-50 text-gray-400 ring-gray-200',
    done_late: 'bg-rose-50/60 text-rose-500 ring-rose-100',
    done_on_time: 'bg-emerald-50/60 text-emerald-600 ring-emerald-100',
};

/** Prazo (SLA) configurado em Configurações → Prazos das tarefas. */
function SlaChip({ task }: { task: ShipmentTaskRow }) {
    const st = task.sla_status;
    if (!st) return null;
    const title = task.due_date
        ? `Prazo de ${task.sla_days} dia(s) · vence ${new Date(task.due_date + 'T12:00:00').toLocaleDateString('pt-BR')}`
        : `Prazo de ${task.sla_days} dia(s)`;
    return (
        <span
            title={title}
            className={`ml-2 inline-flex items-center whitespace-nowrap rounded-full px-1.5 py-0.5 align-middle text-[10px] font-semibold ring-1 ring-inset ${SLA_TONE[st.state] ?? SLA_TONE.on_time}`}
        >
            {st.state === 'waiting' ? `${task.sla_days} d · aguardando início` : st.label}
        </span>
    );
}
